import {
  AvatarGroup,
  Badge,
  Button,
  ButtonGroup,
  Center,
  Flex,
  IconButton,
  Image,
  LinkBox,
  LinkOverlay,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { mergeRelaySets } from "applesauce-core/helpers";
import { Rumor } from "applesauce-common/helpers";
import { GiftWrapsModel, LegacyMessagesGroups, WrappedMessagesGroups } from "applesauce-common/models";
import { useActiveAccount, useEventModel, useObservableEagerState, useObservableState } from "applesauce-react/hooks";
import { NostrEvent, kinds } from "nostr-tools";
import { npubEncode, decode as nip19Decode } from "nostr-tools/nip19";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { LuPenLine, LuQrCode } from "react-icons/lu";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList, ListChildComponentProps } from "react-window";

import { SettingsIcon } from "../../components/icons";
import QRCodeScannerButton from "../../components/qr-code/qr-code-scanner-button";
import QrCodeSvg from "../../components/qr-code/qr-code-svg";
import { CopyIconButton } from "../../components/copy-icon-button";
import UserAutocomplete from "../../components/user-autocomplete";
import { LOCAL_RELAY_URL } from "../../const";
import { nostrRequest } from "../../services/pool";
import { eventStore } from "../../services/event-store";
import SimpleParentView from "../../components/layout/presets/simple-parent-view";
import RequireActiveAccount from "../../components/router/require-active-account";
import Timestamp from "../../components/timestamp";
import UserAvatar from "../../components/user/user-avatar";
import UserName from "../../components/user/user-name";
import useEventIntersectionRef from "../../hooks/use-event-intersection-ref";
import { useLegacyMessagePlaintext } from "../../hooks/use-legacy-message-plaintext";
import useScrollRestoreRef from "../../hooks/use-scroll-restore";
import { useTimelineCurserIntersectionCallback } from "../../hooks/use-timeline-cursor-intersection-callback";
import useTimelineLoader from "../../hooks/use-timeline-loader";
import { useUserInbox } from "../../hooks/use-user-mailboxes";
import IntersectionObserverProvider from "../../providers/local/intersection-observer";
import RequireDecryptionCache from "../../providers/route/require-decryption-cache";
import { legacyMessageSubscription, wrappedMessageSubscription } from "../../services/lifecycle";
import localSettings from "../../services/preferences";
import { DirectMessageRelaysModel } from "../../models/messages";
import ReadAuthRequiredAlert from "./components/read-auth-required-alert";

function MessagePreview({ message }: { message: NostrEvent }) {
  const { plaintext } = useLegacyMessagePlaintext(message);
  return plaintext ? <Text isTruncated>{plaintext || "<Encrypted>"}</Text> : <Badge variant="subtle">Encrypted</Badge>;
}

function ConversationCard({ index, style, data }: ListChildComponentProps<(LegacyGroup | WrappedGroup)[]>) {
  const account = useActiveAccount()!;
  const conversation = data[index];

  const location = useLocation();
  const lastMessage = conversation.lastMessage;

  const ref = useEventIntersectionRef(lastMessage as NostrEvent);
  const others = conversation.participants.filter((p) => p !== account.pubkey);

  return (
    <LinkBox
      as={Flex}
      ref={ref}
      style={style}
      gap="3"
      overflow="hidden"
      px="3"
      py="2"
      _hover={{ bg: "whiteAlpha.50" }}
      cursor="pointer"
      borderBottomWidth="1px"
      borderColor="whiteAlpha.50"
    >
      <AvatarGroup size="md" max={2}>
        {others.map((pubkey) => (
          <UserAvatar key={pubkey} pubkey={pubkey} />
        ))}
      </AvatarGroup>
      <Flex direction="column" gap="0.5" overflow="hidden" flex={1} justifyContent="center">
        <Flex gap="2" alignItems="center" overflow="hidden" w="full">
          <Text fontSize="sm" fontWeight="semibold" isTruncated>
            {others.map((pubkey, index) => (
              <span key={pubkey}>
                <UserName pubkey={pubkey} />
                {index < others.length - 1 && ", "}
              </span>
            ))}
          </Text>
          <Timestamp flexShrink={0} timestamp={lastMessage.created_at} ml="auto" fontSize="xs" color="gray.500" />
        </Flex>
        <Text fontSize="xs" color="gray.500" isTruncated>
          {lastMessage.kind === kinds.EncryptedDirectMessage ? (
            <MessagePreview message={lastMessage as NostrEvent} />
          ) : (
            lastMessage.content
          )}
        </Text>
      </Flex>
      <LinkOverlay
        as={RouterLink}
        to={
          others.length > 1
            ? `/messages/group/${others.map(npubEncode).join(":")}`
            : `/messages/${others.map(npubEncode).join(":")}` + location.search
        }
      />
    </LinkBox>
  );
}

type LegacyGroup = NonNullable<{ id: string; participants: string[]; lastMessage: NostrEvent }>;
type WrappedGroup = NonNullable<{ id: string; participants: string[]; lastMessage: Rumor }>;

function Groups({ onCompose }: { onCompose: () => void }) {
  const account = useActiveAccount()!;

  // Subscribe to incoming messages
  useObservableState(legacyMessageSubscription);
  useObservableState(wrappedMessageSubscription);

  // Create a timeline loader for legacy messages
  const legacyInboxes = useUserInbox(account.pubkey);
  const messagesInboxes = useEventModel(DirectMessageRelaysModel, [account.pubkey]);
  const inboxes = useMemo(() => mergeRelaySets(legacyInboxes, messagesInboxes), [legacyInboxes, messagesInboxes]);
  const { loader } = useTimelineLoader(`${account.pubkey}-legacy-messages`, legacyInboxes ?? [], [
    { authors: [account.pubkey], kinds: [kinds.EncryptedDirectMessage] },
    { "#p": [account.pubkey], kinds: [kinds.EncryptedDirectMessage] },
  ]);

  // Start the legacy messages timeline
  useEffect(() => {
    loader?.();
  }, [loader]);

  // v5: useEventModel expects array of arguments
  const legacyGroups = useEventModel(LegacyMessagesGroups, [account.pubkey]) ?? [];
  const wrappedGroups = useEventModel(WrappedMessagesGroups, [account.pubkey]) ?? [];

  const groups = useMemo(() => {
    const byId = new Map<string, LegacyGroup | WrappedGroup>();
    const all: (LegacyGroup | WrappedGroup)[] = [...(legacyGroups ?? []), ...(wrappedGroups ?? [])];

    for (const group of all) {
      const existing = byId.get(group.id);
      if (existing) {
        if (existing.lastMessage.created_at < group.lastMessage.created_at) byId.set(group.id, group);
      } else byId.set(group.id, group);
    }

    return Array.from(byId.values()).sort((a, b) => b.lastMessage.created_at - a.lastMessage.created_at);
  }, [legacyGroups, wrappedGroups]);

  const scroll = useScrollRestoreRef("chats");
  const callback = useTimelineCurserIntersectionCallback(loader);

  if (groups.length === 0) {
    return (
      <Center flex={1} p="4">
        <VStack spacing="4">
          <Image src="/filler/letter.png" h="120px" objectFit="contain" />
          <VStack spacing="1">
            <Text fontSize="lg" fontWeight="medium">
              No messages yet
            </Text>
            <Text fontSize="sm" color="gray.500" textAlign="center" maxW="xs">
              Tap the button below to send an encrypted message to anyone on the server.
            </Text>
          </VStack>
          <Button leftIcon={<LuPenLine />} colorScheme="primary" onClick={onCompose}>
            New message
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <IntersectionObserverProvider callback={callback}>
      <ReadAuthRequiredAlert relays={inboxes} flexShrink={0} />
      <Flex flex={1} overflow="hidden" position="relative">
        <AutoSizer>
          {({ width, height }) => (
            <FixedSizeList
              height={height}
              width={width}
              itemData={groups ?? []}
              itemCount={groups?.length ?? 0}
              itemKey={(i, data) => data[i].id}
              itemSize={64}
              innerRef={scroll}
              overscanCount={10}
            >
              {ConversationCard}
            </FixedSizeList>
          )}
        </AutoSizer>
      </Flex>
    </IntersectionObserverProvider>
  );
}

function ServerUsersList({ onSelect }: { onSelect: (pubkey: string) => void }) {
  const account = useActiveAccount()!;
  const [users, setUsers] = useState<string[]>([]);

  useEffect(() => {
    const pubkeys = new Set<string>();
    const sub = nostrRequest([LOCAL_RELAY_URL], [{ kinds: [kinds.Metadata] }]).subscribe({
      next: (event) => {
        eventStore.add(event);
        if (event.pubkey !== account.pubkey) pubkeys.add(event.pubkey);
      },
      complete: () => setUsers(Array.from(pubkeys)),
    });
    return () => sub.unsubscribe();
  }, [account.pubkey]);

  if (users.length === 0) return null;

  return (
    <VStack align="stretch" spacing="0" maxH="sm" overflowY="auto">
      <Text fontSize="xs" color="gray.500" px="2" py="1">
        People on this server
      </Text>
      {users.map((pubkey) => (
        <Flex
          key={pubkey}
          p="2"
          gap="2"
          alignItems="center"
          cursor="pointer"
          borderRadius="md"
          _hover={{ bg: "gray.100", _dark: { bg: "gray.700" } }}
          onClick={() => onSelect(pubkey)}
        >
          <UserAvatar pubkey={pubkey} size="sm" />
          <UserName fontWeight="bold" pubkey={pubkey} />
        </Flex>
      ))}
    </VStack>
  );
}

function MessagesHomePage() {
  const account = useActiveAccount()!;
  const navigate = useNavigate();
  const toast = useToast();
  const compose = useDisclosure();
  const myQr = useDisclosure();

  // Automatically decrypt new wrapped messages
  const autoDecryptMessages = useObservableEagerState(localSettings.autoDecryptMessages);
  const locked = useEventModel(GiftWrapsModel, [account.pubkey, true]);

  const myNpub = useMemo(() => npubEncode(account.pubkey), [account.pubkey]);

  const handleScanResult = useCallback(
    (data: string) => {
      let pubkey: string | null = null;

      // Try to parse as npub or nostr: URI
      const cleaned = data.replace(/^nostr:/i, "").trim();
      try {
        const decoded = nip19Decode(cleaned);
        if (decoded.type === "npub") {
          pubkey = decoded.data;
        } else if (decoded.type === "nprofile") {
          pubkey = decoded.data.pubkey;
        }
      } catch {}

      // Try as raw hex pubkey (64 chars)
      if (!pubkey && /^[0-9a-f]{64}$/i.test(cleaned)) {
        pubkey = cleaned;
      }

      if (pubkey) {
        navigate(`/messages/${npubEncode(pubkey)}`);
      } else {
        toast({ title: "Not a valid user QR code", status: "warning", duration: 3000 });
      }
    },
    [navigate, toast],
  );

  return (
    <>
      <SimpleParentView
        path="/messages"
        width="md"
        title="Messages"
        scroll={false}
        actions={
          <ButtonGroup variant="ghost" ms="auto" spacing="0">
            <QRCodeScannerButton onResult={handleScanResult} size="sm" />
            <IconButton
              aria-label="My QR code"
              icon={<LuQrCode />}
              onClick={myQr.onOpen}
              title="Show my QR code"
              size="sm"
            />
            <IconButton
              aria-label="New message"
              icon={<LuPenLine />}
              onClick={compose.onOpen}
              title="New message"
              size="sm"
            />
            <IconButton
              as={RouterLink}
              to="/settings/messages"
              aria-label="Settings"
              icon={<SettingsIcon boxSize={5} />}
              size="sm"
            />
          </ButtonGroup>
        }
      >
        <Groups onCompose={compose.onOpen} />
      </SimpleParentView>

      {/* My QR Code modal */}
      <Modal isOpen={myQr.isOpen} onClose={myQr.onClose} size="sm" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">My Message QR</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6">
            <VStack spacing="4">
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Let someone scan this to start a conversation with you
              </Text>
              <QrCodeSvg
                content={myNpub}
                borderRadius="xl"
                overflow="hidden"
                w="full"
                maxW="280px"
                mx="auto"
              />
              <Flex align="center" gap="2" w="full">
                <Text fontSize="xs" fontFamily="mono" color="gray.500" isTruncated flex="1">
                  {myNpub}
                </Text>
                <CopyIconButton value={myNpub} aria-label="Copy npub" size="sm" />
              </Flex>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* New message modal */}
      <Modal isOpen={compose.isOpen} onClose={compose.onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New Message</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6">
            <UserAutocomplete
              placeholder="Search for a user..."
              hex
              autoFocus
              onSelectUser={(pubkey) => {
                compose.onClose();
                navigate(`/messages/${npubEncode(pubkey)}`);
              }}
            />
            <ServerUsersList
              onSelect={(pubkey) => {
                compose.onClose();
                navigate(`/messages/${npubEncode(pubkey)}`);
              }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}

export default function MessagesHomeView() {
  return (
    <RequireActiveAccount>
      <RequireDecryptionCache>
        <MessagesHomePage />
      </RequireDecryptionCache>
    </RequireActiveAccount>
  );
}
